<template>
    <nav class="c-pagination-wrapper" aria-label="Page navigation">
        <ul class="pagination justify-content-center m-0">
            <li class="page-item" aria-label="first page" :class="{ disabled: currentPage == 1 }" @click="goToPage(1)">
                <span class="page-link" role="button">
                    <span aria-hidden="true">&laquo;</span>
                </span>
            </li>
            <li
                class="page-item"
                aria-label="previous"
                :class="{ disabled: currentPage == 1 }"
                @click="goToPage(currentPage - 1)"
            >
                <span class="page-link" role="button">
                    <span aria-hidden="true">&lsaquo;</span>
                </span>
            </li>
            <li
                v-for="page in displayedPages"
                :key="`page-${page}`"
                class="page-item"
                :class="{ active: page == currentPage }"
            >
                <button class="page-link" role="button" @click="goToPage(page)">{{ page }}</button>
            </li>
            <li class="page-item" :class="{ disabled: currentPage == totalPages }" @click="goToPage(currentPage + 1)">
                <span class="page-link" aria-label="next">
                    <span aria-hidden="true"> &rsaquo; </span>
                </span>
            </li>
            <li class="page-item" :class="{ disabled: currentPage == totalPages }" @click="goToPage(totalPages)">
                <span class="page-link" role="button" aria-label="last page">
                    <span aria-hidden="true">&raquo;</span>
                </span>
            </li>
        </ul>
    </nav>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { PageData } from '.';

const props = withDefaults(
    defineProps<{
        value?: number;
        totalCount: number;
        pageCount?: number;
        btnRangeQty?: number;
    }>(),
    { btnRangeQty: 2, pageCount: 10, value: 1 }
);

const emit = defineEmits<{
    (event: 'input', value: number): void;
    (event: 'page-change', value: PageData): void;
}>();

const currentPage = computed({
    get: () => props.value,
    set: (value: number) => {
        if (props.value == value || !value || value > totalPages.value) return;

        emit('input', value);
        emit('page-change', {
            page: value,
            from: (value - 1) * props.pageCount
        });
    }
});

const totalPages = computed(() => Math.ceil(props.totalCount / props.pageCount));

const displayedPages = computed(() => {
    const start = Math.max(1, currentPage.value - props.btnRangeQty);
    const end = Math.min(totalPages.value, currentPage.value + props.btnRangeQty);

    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
});

const goToPage = (pageNumber: number) => {
    currentPage.value = pageNumber;
};
</script>
